import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { InstallationFeaturesStore } from '@store';
import {
  ActivateDeactivateCommands,
  CirculationPumpProperties,
  DhwConfigurationPumpsCirculationCycleConfigurationMode,
  DhwConfigurationPumpsCirculationMode,
  DhwConfigurationPumpsCirculationTriggerCycleCommands,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  IsActiveProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
} from '@vi-iot/client';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SemanticValues } from 'src/app/+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-constants';
@Component({
  selector: 'app-recirculation-settings',
  templateUrl: './recirculation-settings.component.html',
  styleUrls: ['./recirculation-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecirculationSettingsComponent implements OnInit {
  dhwPumpsCirculation$: Observable<Feature<CirculationPumpProperties, never>>;
  dhwConfigurationPumpsCirculationMode$: Observable<
    Feature<IsStringValueProperty, DhwConfigurationPumpsCirculationMode>
  >;
  dhwConfigurationPumpsCirculationTriggerCycleDuration$: Observable<
    Feature<IsNumberValueProperty, DhwConfigurationPumpsCirculationTriggerCycleCommands>
  >;
  dhwConfigurationPumpsCirculationParallelHygiene$: Observable<
    Feature<IsActiveProperty, ActivateDeactivateCommands>
  >;
  dhwConfigurationPumpsCirculationParallelDhwHeating$: Observable<
    Feature<IsActiveProperty, ActivateDeactivateCommands>
  >;
  dhwConfigurationPumpsCirculationCycleConfiguration$: Observable<
    Feature<IsStringValueProperty, DhwConfigurationPumpsCirculationCycleConfigurationMode>
  >;

  dataOfAvailableTriggeredCycleDuration: {
    unit: string;
    value: number;
  }[];
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureCommands = FeatureCommands;

  ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(private installationFeaturesStore: InstallationFeaturesStore) {}

  private availableTriggeredCycleDuration(
    feature: Feature<IsNumberValueProperty, DhwConfigurationPumpsCirculationTriggerCycleCommands>
  ): void {
    const triggeredCycleDurationArray: {
      unit: string;
      value: number;
    }[] = [];
    const constraints = feature?.commands?.setValue?.params?.value?.constraints;
    const min = constraints?.min;
    const max = constraints?.max;
    const stepping = constraints?.stepping;

    const quantityOfValues = (max - min) / stepping;
    for (let i = 0; i <= quantityOfValues; i++) {
      triggeredCycleDurationArray.push({
        unit: SemanticValues.MINUTE,
        value: stepping * (i + 1),
      });
    }

    this.dataOfAvailableTriggeredCycleDuration = triggeredCycleDurationArray;
  }

  onCirculationModeChange(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwConfigurationPumpsCirculationMode,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode
    );
  }

  onCirculationCycleConfigurationChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwConfigurationPumpsCirculationCycleConfiguration,
      value,
      FeatureActionFields.mode,
      '',
      ''
    );
  }

  onTriggerCycleDurationChange(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwConfigurationPumpsCirculationTriggerCycleDuration,
      value,
      FeaturePropertyKey.value,
      '',
      ''
    );
  }

  onActiveDuringDHWHeating(event: MouseEvent, value: boolean): void {
    event.preventDefault();
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwConfigurationPumpsCirculationParallelDhwHeating,
      value,
      this.featurePropertyKey.useApproved,
      '',
      value ? FeatureCommands.activate : FeatureCommands.deactivate
    );
  }

  onActiveDuringHygiene(event: MouseEvent, value: boolean): void {
    event.preventDefault();
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwConfigurationPumpsCirculationParallelHygiene,
      value,
      this.featurePropertyKey.useApproved,
      '',
      value ? FeatureCommands.activate : FeatureCommands.deactivate
    );
  }

  ngOnInit(): void {
    this.dhwPumpsCirculation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwPumpsCirculation
    );
    this.dhwConfigurationPumpsCirculationMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwConfigurationPumpsCirculationMode
    );
    this.dhwConfigurationPumpsCirculationTriggerCycleDuration$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwConfigurationPumpsCirculationTriggerCycleDuration
    );
    this.dhwConfigurationPumpsCirculationParallelHygiene$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwConfigurationPumpsCirculationParallelHygiene
    );
    this.dhwConfigurationPumpsCirculationParallelDhwHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwConfigurationPumpsCirculationParallelDhwHeating
    );
    this.dhwConfigurationPumpsCirculationCycleConfiguration$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwConfigurationPumpsCirculationCycleConfiguration
    );

    this.dhwConfigurationPumpsCirculationTriggerCycleDuration$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((feature) => !!feature)
      )
      .subscribe((feature) => {
        this.availableTriggeredCycleDuration(feature);
      });
  }
}
